import * as React from 'react';

import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ThemeProvider,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Link,
  Route,
  Routes,
  matchPath,
  useLocation,
  useNavigate,
  useNavigationType,
} from 'react-router-dom';

import Footer from './components/Footer';
import ItemButton from './components/ItemButton';
import RouteMetadata from './components/RouteMetadata';
import routes from './data/Navigation';
import theme from './theme';
import './App.css';

export default function Demo() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const focusAfterClose = React.useRef<'main' | 'menu' | null>(null);
  const menuButtonRef = React.useRef<HTMLButtonElement>(null);
  const mainRef = React.useRef<HTMLElement>(null);
  const location = useLocation();
  const previousLocation = React.useRef<typeof location | null>(null);
  const destinationRef = React.useRef<HTMLElement | null>(null);
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const isTabletOrDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const showFooter = routes.some(
    (route) =>
      route.footer && Boolean(matchPath({ path: route.path, end: true }, location.pathname))
  );

  React.useEffect(() => {
    if (isTabletOrDesktop && drawerOpen) {
      destinationRef.current = mainRef.current;
      focusAfterClose.current = 'main';
      setDrawerOpen(false);
    }
  }, [isTabletOrDesktop, drawerOpen]);

  React.useEffect(() => {
    const previous = previousLocation.current;
    if (
      previous?.key === location.key &&
      previous.pathname === location.pathname &&
      previous.hash === location.hash
    ) {
      return;
    }
    previousLocation.current = location;

    // Only published case-study articles are section destinations. Other fragments,
    // including the native skip link, keep their existing browser behavior.
    let section: HTMLElement | null = null;
    if (location.pathname.replace(/\/$/, '') === '/case-studies' && location.hash) {
      try {
        const candidate = document.getElementById(decodeURIComponent(location.hash.slice(1)));
        if (candidate?.matches('article[tabindex="-1"]') && mainRef.current?.contains(candidate)) {
          section = candidate;
        }
      } catch {
        // A malformed URL fragment should not interrupt navigation.
      }
    }

    const changed =
      previous && (previous.pathname !== location.pathname || previous.hash !== location.hash);
    if (!section && !changed) return;

    const destination = section ?? mainRef.current;
    destinationRef.current = destination;
    if (!previous || navigationType !== 'POP') {
      if (section) section.scrollIntoView({ block: 'start' });
      else window.scrollTo(0, 0);
    }

    if (drawerOpen || focusAfterClose.current) {
      focusAfterClose.current = 'main';
      if (drawerOpen) setDrawerOpen(false);
    } else {
      destination?.focus({ preventScroll: true });
    }
  }, [location, drawerOpen, navigationType]);

  const closeAndNavigate = (path: string) => {
    destinationRef.current = mainRef.current;
    focusAfterClose.current = 'main';
    setDrawerOpen(false);
    navigate(path);
  };

  const handleKeyPress = (path: string) => (event: React.KeyboardEvent) => {
    if (
      (event.key === 'Enter' || event.key === ' ') &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.shiftKey &&
      !event.altKey
    ) {
      event.preventDefault();
      closeAndNavigate(path);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <RouteMetadata />
      <a
        href='#main-content'
        className='skip-link'
      >
        Skip to main content
      </a>
      <AppBar
        position='sticky'
        elevation={0}
        sx={{ backgroundColor: '#0B2545', borderBottom: '1px solid rgba(0, 180, 216, 0.2)' }}
      >
        <Toolbar
          sx={{
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            px: { sm: 2, md: 3 },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
            <IconButton
              ref={menuButtonRef}
              onClick={() => {
                focusAfterClose.current = null;
                setDrawerOpen(true);
              }}
              edge='start'
              color='inherit'
              aria-label='menu'
              aria-controls={drawerOpen && !isTabletOrDesktop ? 'mobile-navigation' : undefined}
              aria-expanded={drawerOpen && !isTabletOrDesktop}
              sx={{ mr: 1, display: { xs: 'inline-flex', sm: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            <Box
              component={Link}
              to='/'
              sx={{ display: 'flex', alignItems: 'center', minWidth: 0, textDecoration: 'none' }}
            >
              <Box
                component='img'
                src='/brand/mark-reversed.svg'
                alt=''
                aria-hidden='true'
                sx={{ height: 38, width: 38, mr: 1.5 }}
              />
              <Typography
                className='appBarTitle'
                component='span'
                variant='h6'
                sx={{
                  color: '#FFFFFF',
                  display: 'flex',
                  gap: 0.5,
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  fontSize: { xs: 'clamp(0.9rem, 4vw, 1.1rem)', sm: '1.35rem' },
                }}
              >
                <Box
                  component='span'
                  sx={{ color: '#4CD6FB' }}
                >
                  Scaled
                </Box>
                <Box component='span'>Tech</Box>
                <Box
                  component='span'
                  sx={{ color: '#94A3B8', display: { xs: 'none', md: 'inline' } }}
                >
                  Consulting
                </Box>
              </Typography>
            </Box>
          </Box>
          <Box
            component='nav'
            aria-label='Primary navigation'
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexWrap: 'wrap',
              maxWidth: '100%',
              alignItems: 'center',
              gap: { sm: 0.5, md: 1 },
            }}
          >
            {routes
              .filter((route) => route.sidebar)
              .map((route) => {
                const selected = Boolean(
                  matchPath({ path: route.path, end: true }, location.pathname)
                );
                return (
                  <Button
                    key={route.path}
                    component={Link}
                    to={route.path}
                    aria-current={selected ? 'page' : undefined}
                    variant={route.path === '/contact-us' ? 'contained' : 'text'}
                    sx={{
                      color: '#FFFFFF',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      px: { sm: 0.5, md: 1.5 },
                      fontSize: { sm: '0.8rem', md: '0.875rem' },
                      backgroundColor: route.path === '/contact-us' ? '#005596' : undefined,
                      borderBottom: selected ? '2px solid #4CD6FB' : '2px solid transparent',
                      borderRadius: route.path === '/contact-us' ? 1 : 0,
                    }}
                  >
                    {route.topLabel}
                  </Button>
                );
              })}
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor='left'
        open={drawerOpen && !isTabletOrDesktop}
        onClose={() => {
          focusAfterClose.current = 'menu';
          setDrawerOpen(false);
        }}
        ModalProps={{ disableRestoreFocus: true }}
        PaperProps={{ sx: { scrollPaddingTop: '16px', scrollPaddingBottom: '16px' } }}
        SlideProps={{
          onExited: () => {
            if (focusAfterClose.current === 'main' || isTabletOrDesktop) {
              (destinationRef.current ?? mainRef.current)?.focus({ preventScroll: true });
            } else {
              menuButtonRef.current?.focus();
            }
            focusAfterClose.current = null;
          },
        }}
      >
        <Box
          component='nav'
          id='mobile-navigation'
          aria-label='Mobile navigation'
          sx={{ width: 280, pt: 2 }}
        >
          <List>
            {routes
              .filter((route) => route.sidebar)
              .map((route) => (
                <ListItem
                  key={route.path}
                  disablePadding
                >
                  <ItemButton
                    label1={route.label1}
                    label2={route.label2}
                    path={route.path}
                    icon={route.icon}
                    isSelected={Boolean(
                      matchPath({ path: route.path, end: true }, location.pathname)
                    )}
                    onClick={closeAndNavigate}
                    onPress={handleKeyPress}
                  />
                </ListItem>
              ))}
          </List>
        </Box>
      </Drawer>
      <Box
        component='main'
        id='main-content'
        ref={mainRef}
        tabIndex={-1}
        sx={{ minHeight: showFooter ? 'auto' : 'calc(100vh - 100px)' }}
      >
        <Routes>
          {routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<route.component />}
            />
          ))}
        </Routes>
      </Box>
      {showFooter && (
        <Container
          maxWidth='lg'
          sx={{ pb: 8 }}
        >
          <Footer />
        </Container>
      )}
    </ThemeProvider>
  );
}
